import { DrawerProps, Modal, ModalProps } from 'antd';
import { ReactNode, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTheme } from 'styled-components';

import { StyledDrawerModalTitle, getStyles } from './DrawerModal.style';
import BottomSheet from './components/BottomSheet/BottomSheet';
import { CloseButton, CloseIconButtonProps } from './components/CloseIconButton/CloseButon';

export type DrawerModalProps = {
  content: DrawerProps['children'] | ModalProps['children'];
  isOpened: DrawerProps['open'] | ModalProps['open'];
  title?: DrawerProps['title'] | ModalProps['title'];
  onClose?: DrawerProps['onClose'] | CloseIconButtonProps['onClick'];
  type?: CloseIconButtonProps['type'];
  footer?: DrawerProps['footer'] | ModalProps['footer'];
  width?: ModalProps['width'];
  height?: string | number;
  minHeight?: string | number;
  areBordersHidden?: boolean;
  isClosingDisabled?: boolean;
  afterOpenChange?: DrawerProps['afterOpenChange'] | ModalProps['afterOpenChange'];
  position?: {
    top: number;
    right: number;
  };
  hasCloseBar?: boolean;
};

export const DrawerModal = ({
  content,
  isOpened,
  title,
  onClose,
  type,
  width,
  height,
  minHeight,
  footer = null,
  areBordersHidden = false,
  isClosingDisabled = false,
  afterOpenChange,
  position,
  hasCloseBar,
}: DrawerModalProps) => {
  const theme = useTheme();
  const styles = getStyles(theme, areBordersHidden, height, minHeight);
  const isMobile = useMediaQuery({ maxWidth: theme.Screen.Size.Small });
  const isScreenLarge = useMediaQuery({ minWidth: theme.Screen.Size.Large });

  const relativePosition = useMemo(() => {
    if (!position || !width) {
      return;
    }

    const centerX = window.innerWidth / 2;
    const right = centerX + Number(width) / 2;

    return {
      top: position.top,
      right: right - position.right,
    };
  }, [position, width]);

  const CloseIcon = useMemo<JSX.Element>(
    () => <CloseButton onClick={onClose as CloseIconButtonProps['onClick']} type={type} />,
    [onClose, type]
  );

  const Title = useMemo(() => {
    return title ? (
      <StyledDrawerModalTitle>
        {title}
        {CloseIcon}
      </StyledDrawerModalTitle>
    ) : null;
  }, [title, CloseIcon]);

  return isMobile ? (
    <BottomSheet
      title={Title}
      open={isOpened}
      footer={footer as ReactNode}
      maskClosable={!isClosingDisabled}
      keyboard={!isClosingDisabled}
      onClose={onClose as DrawerProps['onClose']}
      afterOpenChange={afterOpenChange}
      hasCloseBar={hasCloseBar}
    >
      {content}
    </BottomSheet>
  ) : (
    <Modal
      title={Title}
      open={isOpened}
      closeIcon={null}
      centered={!relativePosition}
      footer={footer}
      onCancel={onClose}
      width={width}
      maskClosable={!isClosingDisabled}
      keyboard={!isClosingDisabled}
      styles={{ ...styles.modal }}
      destroyOnHidden
      afterOpenChange={afterOpenChange}
      style={{
        top: relativePosition?.top,
        right: relativePosition?.right,
      }}
    >
      {content}
      {type === 'floating' && isScreenLarge && CloseIcon}
    </Modal>
  );
};

export default DrawerModal;
